import React, { memo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Autocomplete,
  IconButton,
  Collapse,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FormatListNumbered as SubPartsIcon,
} from '@mui/icons-material';
import {
  QuestionPaperQuestion,
} from '../../services/generateQuestionPaperService';
import {
  Chapter,
  QuestionType,
  QuestionImportance,
} from '../../services/questionPaperService';
import MathInput from '../shared/MathInput';
import { elegantMenuProps } from '../../utils/menuStyles';

const toRoman = (num: number): string => {
  const romanNumerals: [number, string][] = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']
  ];
  let result = '';
  for (const [value, numeral] of romanNumerals) {
    while (num >= value) { result += numeral; num -= value; }
  }
  return result.toLowerCase();
};

interface CustomQuestionDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<QuestionPaperQuestion>, editingIndex: number | null) => void;
  editingIndex: number | null;
  initialData: Partial<QuestionPaperQuestion> | null;
  chapters: Chapter[];
  hasClassAndSubject: boolean;
}

// Fields on `QuestionPaperQuestion` are unified with the bank Question —
// there is no more `custom*` shadow set. The dialog writes directly into
// the snapshot fields on the paper row.
const defaultFormData: Partial<QuestionPaperQuestion> = {
  questionText: '',
  type: QuestionType.MCQ,
  importance: QuestionImportance.MEDIUM,
  options: ['', '', '', ''],
  correctAnswer: '',
  answer: '',
  subParts: undefined,
  marks: 1,
  chapterId: '',
  difficulty: '',
};


const CustomQuestionDialog = memo<CustomQuestionDialogProps>(({
  open,
  onClose,
  onSave,
  editingIndex,
  initialData,
  chapters,
  hasClassAndSubject,
}) => {
  const [form, setForm] = useState<Partial<QuestionPaperQuestion>>(defaultFormData);

  useEffect(() => {
    if (open) {
      setForm(initialData || defaultFormData);
    }
  }, [open, initialData]);

  const handleSave = useCallback(() => {
    onSave(form, editingIndex);
  }, [form, editingIndex, onSave]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{editingIndex !== null ? 'Edit Question' : 'Add Custom Question'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <MathInput
                fullWidth
                label="Question Text"
                required
                multiline
                rows={3}
                value={form.questionText || ''}
                onChange={(val) =>
                  setForm({ ...form, questionText: val })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select MenuProps={elegantMenuProps}
                  value={form.type}
                  label="Type"
                  onChange={(e) =>
                    setForm({ ...form, type: e.target.value as QuestionType })
                  }
                >
                  {Object.values(QuestionType).map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Importance</InputLabel>
                <Select MenuProps={elegantMenuProps}
                  value={form.importance}
                  label="Importance"
                  onChange={(e) =>
                    setForm({ ...form, importance: e.target.value as QuestionImportance })
                  }
                >
                  {Object.values(QuestionImportance).map((imp) => (
                    <MenuItem key={imp} value={imp}>{imp}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Marks"
                required
                value={form.marks}
                onChange={(e) =>
                  setForm({ ...form, marks: parseInt(e.target.value) || 0 })
                }
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={chapters}
                getOptionLabel={(option) => option.name || ''}
                value={chapters.find(ch => ch._id === form.chapterId) || null}
                onChange={(_event, newValue) => {
                  setForm({
                    ...form,
                    chapterId: newValue ? newValue._id : ''
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Link to Chapter (Optional)"
                    placeholder={hasClassAndSubject
                      ? (chapters.length > 0 ? "Search chapters..." : "No chapters found")
                      : "Select class and subject first"}
                  />
                )}
                noOptionsText={hasClassAndSubject
                  ? "No chapters found"
                  : "Select class and subject first"}
                disabled={!hasClassAndSubject}
              />
            </Grid>

            {/* MCQ Options */}
            {form.type === QuestionType.MCQ && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>Options</Typography>
                {form.options?.map((option, idx) => (
                  <MathInput
                    key={idx}
                    fullWidth
                    size="small"
                    label={`Option ${idx + 1}`}
                    value={option}
                    onChange={(val) => {
                      const newOptions = [...(form.options || [])];
                      newOptions[idx] = val;
                      setForm({ ...form, options: newOptions });
                    }}
                    multiline={false}
                    sx={{ mb: 1 }}
                  />
                ))}
                <MathInput
                  fullWidth
                  size="small"
                  label="Correct Answer"
                  value={form.correctAnswer || ''}
                  onChange={(val) =>
                    setForm({ ...form, correctAnswer: val })
                  }
                  multiline={false}
                />
              </Grid>
            )}

            {/* Fill in the blanks */}
            {form.type === QuestionType.FILL_IN_THE_BLANKS && (
              <Grid size={{ xs: 12 }}>
                <MathInput
                  fullWidth
                  label="Correct Answer"
                  value={form.correctAnswer || ''}
                  onChange={(val) =>
                    setForm({ ...form, correctAnswer: val })
                  }
                  multiline={false}
                />
              </Grid>
            )}

            {/* Short/Long Answer */}
            {(form.type === QuestionType.SHORT || form.type === QuestionType.LONG) && (
              <>
                <Grid size={{ xs: 12 }}>
                  <MathInput
                    fullWidth
                    label="Sample Answer (Optional)"
                    multiline
                    rows={form.type === QuestionType.LONG ? 4 : 2}
                    value={form.answer || ''}
                    onChange={(val) =>
                      setForm({ ...form, answer: val })
                    }
                  />
                </Grid>

                {/* Sub-parts */}
                <Grid size={{ xs: 12 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderStyle: 'dashed',
                      borderColor: form.subParts && form.subParts.length > 0 ? 'primary.main' : 'divider',
                      bgcolor: form.subParts && form.subParts.length > 0 ? 'primary.50' : 'transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={form.subParts && form.subParts.length > 0 ? 1.5 : 0}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <SubPartsIcon fontSize="small" color={form.subParts && form.subParts.length > 0 ? 'primary' : 'action'} />
                        <Typography variant="subtitle2" color={form.subParts && form.subParts.length > 0 ? 'primary' : 'text.secondary'}>
                          Sub-parts {form.subParts && form.subParts.length > 0 ? `(${form.subParts.length})` : '(Optional)'}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setForm({ ...form, subParts: [...(form.subParts || []), { text: '' }] })}
                        sx={{ textTransform: 'none' }}
                      >
                        Add Part
                      </Button>
                    </Box>

                    <Collapse in={!!(form.subParts && form.subParts.length > 0)}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {form.subParts?.map((part, index) => (
                          <Box key={index} display="flex" gap={1} alignItems="flex-start">
                            <Typography
                              variant="body2"
                              sx={{
                                minWidth: 32,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                color: 'primary.main',
                                bgcolor: 'primary.50',
                                borderRadius: 1,
                                fontSize: '0.8rem',
                              }}
                            >
                              ({toRoman(index + 1)})
                            </Typography>
                            <MathInput
                              fullWidth
                              size="small"
                              placeholder={`Sub-part ${toRoman(index + 1)}`}
                              value={part.text}
                              onChange={(val) => {
                                const newParts = [...(form.subParts || [])];
                                newParts[index] = { text: val };
                                setForm({ ...form, subParts: newParts });
                              }}
                              multiline={false}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                // Keep as an explicit empty array (NOT undefined) when
                                // the last subpart is removed, so the backend can tell
                                // "user cleared them" from "user never touched the
                                // field". JSON.stringify drops `undefined`, which would
                                // make the server fall back to the bank's old subparts.
                                const newParts = (form.subParts || []).filter((_, i) => i !== index);
                                setForm({ ...form, subParts: newParts });
                              }}
                              sx={{ mt: 0.25 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Paper>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          {editingIndex !== null ? 'Save Changes' : 'Add Question'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

CustomQuestionDialog.displayName = 'CustomQuestionDialog';

export default CustomQuestionDialog;
